// src/App.js
import { useRef, useState } from "react";
import emailjs from "@emailjs/browser";

import "./App.css";
import ProjectsSection from "./ProjectsSection";

function App() {
  const formRef = useRef(null);

  const [status, setStatus] = useState({ state: "idle", msg: "" });
  // idle | sending | ok | error

  const [cooldownUntil, setCooldownUntil] = useState(0);

  const submitContact = async (e) => {
    e.preventDefault();
    const LIMIT_MS = 5 * 60 * 1000; // 5 minutes
const lastKey = "contact_last_sent_at";
const now = Date.now();
const lastSent = Number(localStorage.getItem(lastKey) || "0");

if (lastSent && now - lastSent < LIMIT_MS) {
  const secsLeft = Math.ceil((LIMIT_MS - (now - lastSent)) / 1000);
  const minLeft = Math.ceil(secsLeft / 60);

  setStatus({
    state: "error",
    msg: `Έχετε στείλει πρόσφατα μήνυμα. Δοκιμάστε ξανά σε ${minLeft} λεπτά.`,
  });
  return;
}

    // --- Anti-spam #1: cooldown (π.χ. 20s) ---
    if (now < cooldownUntil) {
      const secs = Math.ceil((cooldownUntil - now) / 1000);
      setStatus({
        state: "error",
        msg: `Περιμένετε ${secs}″ και δοκιμάστε ξανά.`,
      });
      return;
    }

    // Read raw form values (no controlled state needed)
    const fd = new FormData(formRef.current);

    // --- Anti-spam #2: honeypot field ---
    const hp = (fd.get("website") || "").toString().trim();
    if (hp) {
      // Pretend success, but don't send
      setStatus({
        state: "ok",
        msg: "Το μήνυμά σας στάλθηκε! Θα επικοινωνήσουμε σύντομα.",
      });
      formRef.current.reset();
      setCooldownUntil(Date.now() + 20000);
      return;
    }

    // --- Anti-spam #3: minimum time on page/form (simple “bot speed” check) ---
    // If you want stricter, increase 1200ms
    const startedAt = Number(fd.get("_started_at") || 0);
    if (!startedAt || Date.now() - startedAt < 1200) {
      setStatus({
        state: "error",
        msg: "Δοκιμάστε ξανά σε λίγα δευτερόλεπτα.",
      });
      return;
    }

    // --- Anti-spam #4: basic message length check ---
    const message = (fd.get("message") || "").toString().trim();
    if (message.length < 10) {
      setStatus({
        state: "error",
        msg: "Γράψτε λίγες περισσότερες λεπτομέρειες στο μήνυμα.",
      });
      return;
    }

    setStatus({ state: "sending", msg: "Αποστολή..." });

try {
  await emailjs.sendForm(
    process.env.REACT_APP_EMAILJS_SERVICE_ID,
    process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    formRef.current,
    { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
  );

  localStorage.setItem("contact_last_sent_at", String(Date.now()));

  setStatus({
    state: "ok",
    msg: "Το μήνυμά σας στάλθηκε! Θα επικοινωνήσουμε σύντομα.",
  });

  formRef.current.reset();
} catch (err) {
  setStatus({
    state: "error",
    msg: "Κάτι πήγε στραβά. Δοκιμάστε ξανά ή καλέστε μας.",
  });
}

  };

  return (
    <div className="page">
      {/* Navbar */}
      <header className="navbar">
        <div className="container nav-inner">
          <img
            src="/logo.png"
            alt="Tito Constructions"
            className="logo-img"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          />

          <nav className="nav-links">
            <a href="#services">Υπηρεσίες</a>
            <a href="#projects">Έργα</a>
            <a href="#about">Ποιοι Είμαστε</a>
            <a href="#contact">Επικοινωνία</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-text">
            <h1>
              Tito Constructions
              <br />
              Χτίζουμε Σιγουριά & Ποιότητα
            </h1>
            <p>
              Η Tito Constructions αναλαμβάνει ολοκληρωμένα έργα κατασκευής,
              ανακαίνισης και τεχνικής υποστήριξης, με συνέπεια και υψηλές
              προδιαγραφές.
            </p>
            <div className="hero-actions">
              <a href="#contact" className="btn btn-primary">
                Ζητήστε Προσφορά
              </a>
              <a href="#projects" className="btn btn-outline">
                Δείτε τα έργα μας
              </a>
            </div>
          </div>

          <div className="hero-card">
            <div className="hero-badge">20+ χρόνια εμπειρίας</div>
            <h3>Εξειδικευμένη τεχνική ομάδα</h3>
            <p>
              Από τη μελέτη μέχρι την παράδοση, προσφέρουμε ολοκληρωμένες
              κατασκευαστικές λύσεις με υψηλή ποιότητα και αξιοπιστία.
            </p>
            <ul>
              <li>Οικιστικά & επαγγελματικά κτίρια</li>
              <li>Ανακαινίσεις χώρων</li>
              <li>Τεχνικές επιβλέψεις & μελέτες</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="section">
        <div className="container">
          <h2 className="section-title">Υπηρεσίες</h2>
          <p className="section-subtitle">
            Καλύπτουμε όλο το φάσμα κατασκευών και τεχνικών εργασιών.
          </p>

          <div className="grid">
            <div className="card">
              <h3>Κατασκευή Κτιρίων</h3>
              <p>
                Ανάληψη έργων από τα θεμέλια έως την παράδοση, με έμφαση στη
                ποιότητα και την ασφάλεια.
              </p>
            </div>
            <div className="card">
              <h3>Ανακαινίσεις</h3>
              <p>
                Ανακαινίσεις κατοικιών, επαγγελματικών χώρων και καταστημάτων
                με σύγχρονο design.
              </p>
            </div>
            <div className="card">
              <h3>Μελέτες & Επίβλεψη</h3>
              <p>Αρχιτεκτονικές, στατικές και ηλεκτρομηχανολογικές μελέτες.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <ProjectsSection />

      {/* About */}
      <section id="about" className="section">
        <div className="container about-grid">
          <div>
            <h2 className="section-title">Ποιοι Είμαστε</h2>
            <p>
              Η Tito Constructions δραστηριοποιείται στον χώρο των κατασκευών
              για πάνω από δύο δεκαετίες, με δεκάδες ολοκληρωμένα έργα σε όλη την
              Αττική.
            </p>
            <p>
              Συνδυάζουμε τεχνογνωσία, έμπειρο προσωπικό και επαγγελματισμό,
              δίνοντας έμφαση στην ποιότητα, την ασφάλεια και τη διαφάνεια.
            </p>
          </div>

          <div className="about-highlights">
            <div className="highlight">
              <span className="highlight-number">20+</span>
              <span className="highlight-label">χρόνια εμπειρίας</span>
            </div>
            <div className="highlight">
              <span className="highlight-number">100+</span>
              <span className="highlight-label">ολοκληρωμένα έργα</span>
            </div>
            <div className="highlight">
              <span className="highlight-number">15</span>
              <span className="highlight-label">εξειδικευμένοι συνεργάτες</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section section-alt">
        <div className="container contact-grid">
          <div>
            <h2 className="section-title">Επικοινωνία</h2>
            <p className="section-subtitle">
              Πείτε μας τι χρειάζεστε και θα σας απαντήσουμε άμεσα. <br />
              <strong>Δωρεάν εκτίμηση</strong>
            </p>

            <div className="contact-info">
              <p>
                <strong>Τηλ.:</strong>{" "}
                <a href="tel:+302114094966">+30 211 409 4966</a>
              </p>
              <p>
                <strong>Κιν. Τηλ.:</strong>{" "}
                <a href="tel:+306907183635">+30 690 718 3635</a>
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:Titoconstuctions@gmail.com">
                  Titoconstuctions@gmail.com
                </a>
              </p>
              <p>
                <strong>Διεύθυνση:</strong>{" "}
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Μυκόνου+7+Πειραιάς"
                  target="_blank"
                  rel="noreferrer"
                >
                  Μυκόνου 7, Πειραιάς
                </a>
              </p>
            </div>

            <div
              className="contact-actions"
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                marginTop: 16,
              }}
            >
              <a className="btn btn-primary" href="tel:+306907183635">
                Κλήση τώρα
              </a>
              <a
                className="btn btn-outline"
                href="mailto:Titoconstuctions@gmail.com?subject=Αίτημα%20Προσφοράς"
              >
                Στείλτε Email
              </a>
            </div>
          </div>

          <form ref={formRef} className="contact-form" onSubmit={submitContact}>
  {/* Anti-spam hidden timestamp */}
  <input type="hidden" name="_started_at" value={Date.now()} />

  <div className="form-group">
    <label>Ονοματεπώνυμο</label>
    <input
      type="text"
      name="name"
      required
      placeholder="Πλήρες όνομα"
      autoComplete="name"
    />
  </div>

  <div className="form-group">
    <label>Email</label>
    <input
      type="email"
      name="email"
      required
      placeholder="email@paradeigma.gr"
      autoComplete="email"
    />
  </div>

  <div className="form-group">
    <label>Τηλέφωνο</label>
    <input
      type="tel"
      name="phone"
      placeholder="+30 ..."
      autoComplete="tel"
    />
  </div>

  <div className="form-group">
    <label>Περιοχή έργου</label>
    <input
      type="text"
      name="area"
      placeholder="π.χ. Πειραιάς, Μαρούσι..."
    />
  </div>

  <div className="form-group">
    <label>Μήνυμα</label>
    <textarea
      name="message"
      required
      rows="4"
      placeholder="Περιγράψτε το έργο σας..."
    />
  </div>

  {/* Anti-spam honeypot */}
  <input
    type="text"
    name="website"
    style={{ display: "none" }}
    tabIndex="-1"
    autoComplete="off"
  />

  <button
    type="submit"
    className="btn btn-primary full-width"
    disabled={status.state === "sending"}
  >
    {status.state === "sending" ? "Αποστολή..." : "Αποστολή"}
  </button>

  {status.state !== "idle" && (
    <p style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
      {status.msg}
    </p>
  )}

  <p style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
    Με την αποστολή συμφωνείτε να επικοινωνήσουμε μαζί σας για το αίτημά σας.
  </p>
</form>

        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <span>
            © {new Date().getFullYear()} Tito Constructions. All rights reserved.
          </span>
          <span>Κατασκευή & Design: Fady</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
